import React from "react";
import { Link } from "react-router-dom";
import { useI18n } from "../i18n";

export interface SiteNavHeaderProps {
  workspaceId: string;
  workspaceName?: string;
  siteId: string;
  siteName: string;
  title: string;
  subtitle?: string;
  timeRange?: "24h" | "today" | "7d" | "30d";
  onTimeRangeChange?: (r: "24h" | "today" | "7d" | "30d") => void;
  onRefresh?: () => void;
  onExport?: () => void;
  extraActions?: React.ReactNode;
}

export function SiteNavHeader({
  workspaceId,
  workspaceName,
  siteId,
  siteName,
  title,
  subtitle,
  timeRange,
  onTimeRangeChange,
  onRefresh,
  onExport,
  extraActions,
}: SiteNavHeaderProps) {
  const { t } = useI18n();

  return (
    <div className="site-header-block">
      {/* Breadcrumb row */}
      <div className="site-breadcrumbs">
        <Link to="/" className="site-breadcrumb-link">
          {t.common.workspaces}
        </Link>
        <span className="site-breadcrumb-sep">/</span>
        <Link to={`/w/${workspaceId}`} className="site-breadcrumb-link">
          {workspaceName || "Workspace"}
        </Link>
        <span className="site-breadcrumb-sep">/</span>
        <Link to={`/w/${workspaceId}/sites/${siteId}`} className="site-breadcrumb-link active">
          {siteName}
        </Link>
        <span className="site-breadcrumb-sep">/</span>
        <span className="site-breadcrumb-current">{title}</span>
      </div>

      {/* Main title & Action bar row */}
      <div className="site-title-bar">
        <div>
          <h1 className="site-main-title">{title}</h1>
          {subtitle && <p className="site-subtitle">{subtitle}</p>}
        </div>

        <div className="site-actions-group">
          {/* Time range selector pill */}
          {timeRange && onTimeRangeChange && (
            <div className="time-range-group">
              {(["24h", "today", "7d", "30d"] as const).map((r) => (
                <button
                  key={r}
                  type="button"
                  className={`time-range-pill ${timeRange === r ? "active" : ""}`}
                  onClick={() => onTimeRangeChange(r)}
                >
                  {r === "24h"
                    ? t.siteDetail.range24h
                    : r === "today"
                      ? t.siteDetail.rangeToday
                      : r === "7d"
                        ? t.siteDetail.range7d
                        : t.siteDetail.range30d}
                </button>
              ))}
            </div>
          )}

          {onRefresh && (
            <button
              type="button"
              className="action-btn-secondary"
              title={t.common.refresh}
              onClick={onRefresh}
            >
              🔄 {t.common.refresh}
            </button>
          )}

          {onExport && (
            <button
              type="button"
              className="action-btn-primary"
              onClick={onExport}
            >
              📥 {t.siteDetail.exportBtn}
            </button>
          )}

          {extraActions}
        </div>
      </div>
    </div>
  );
}
